import { useQuery } from '@apollo/client';
import React, { FC, ReactEventHandler } from 'react';
import { Helmet } from 'react-helmet-async';

import PageScroller from 'Components/PageScroller/PageScroller';
import { ArrowRightIcon } from 'common/icons/ArrowRightIcon';
import config from 'config/config';
import { Button } from 'uikit/Button';
import { Heading1 } from 'uikit/Heading';
import { Heading2 } from 'uikit/Heading';
import categoryIcon from 'uikit/Icons/Categories';
import { TextP3 } from 'uikit/Text';
import analytics from 'utils/analytics/analytics';
import routes from 'utils/routeTranslator';

import { categoryUrlBySlug, l2CategoryListQuery, L2CategoryListResponse } from './PayWithFixlyPromo.helpers';
import './PayWithFixlyPromo.scss';
import posterJpg from './poster/poster-460.jpg';
import posterWebp from './poster/poster-460.webp';
import posterWebp2x from './poster/poster-920.webp';
import ogImage from './poster/og-image.jpg';
import postReplacement2x from './poster/replacement2x.webp';
import postReplacement1x from './poster/replacement1x.webp';
import postReplacement from './poster/replacement1x.jpg';

const title = 'Płatności Fixly są obecnie niedostępne';

/* eslint-disable max-len */
const leads = [
  'Płatności Fixly były dostępne do 01.12.2023 roku. Obecnie nie możesz skorzystać z tej formy płatności.',
  'Wciąż jednak możesz rozliczyć się bezpośrednio z Wykonawcą. Wybierz  usługę, wskaż lokalizację, odpowiedz na kilka pytań i wyślij zapytanie. Otrzymasz oferty od zainteresowanych Wykonawców z Twojej okolicy. Porównaj profile, sprawdź opinie i wybierz Wykonawcę, któremu zlecisz realizację usługi.',
  ' Ustal szczegóły realizacji oraz sposób rozliczenia - przelewem lub gotówką. Nie zapomnij poprosić o fakturę, jeśli jest potrzebna.',
];

/* eslint-enable max-len */

const PayWithFixlyPromo: FC = () => {
  const { data } = useQuery<L2CategoryListResponse>(l2CategoryListQuery);

  const trackCategoryClick = (url: string, callback?: () => void) => {
    analytics.trackEvent(
      'pwf_promo_lp_category_selected',
      {
        slug: url,
        touch_point_page: 'pwf_promo_page',
      },
      callback
    );
  };

  const onCategoryClick: ReactEventHandler<HTMLAnchorElement> = (event) => {
    event.preventDefault();
    const slug = event.currentTarget.dataset.slug as string;
    const { href } = event.currentTarget;
    trackCategoryClick(slug.replace('/', ''), () => (window.location.href = href));
  };

  return (
    <main className="container">
      <Helmet>
        <title>{title} - Fixly.pl</title>
        <meta name="description" content={`${leads[0]} ${leads[1]} ${leads[2]}}`} />
        <meta property="og:title" content={title} />
        <meta property="og:description" content="Płatności Fixly są obecnie niedostępne" />
        <meta property="og:image" content={ogImage} />
        <meta property="og:type" content="website" />
        <meta name="thumbnail" content={posterJpg} />
        <link rel="canonical" href={config.SITE_URL + routes.get('front::pwf.promo')} />
      </Helmet>
      <section className="payWithFixlyPromo__section payWithFixlyPromo__section_intro">
        <div className="payWithFixlyPromo__intro">
          <Heading1>{title}</Heading1>
          <TextP3 className="payWithFixlyPromo__lead">{leads[0]}</TextP3>
          <TextP3 className="payWithFixlyPromo__lead">{leads[1]}</TextP3>
          <TextP3 className="payWithFixlyPromo__lead">{leads[2]}</TextP3>
          <div>
            <Button
              as="a"
              href="#services"
              kind="cta"
              onClick={() => analytics.trackEvent('pwf_promo_lp_make_request')}
            >
              Zleć usługę
            </Button>
          </div>
        </div>
        <picture>
          <source srcSet={`${posterWebp} 1x, ${posterWebp2x} 2x`} type="image/webp" />
          <img src={posterJpg} alt="" width="330" height="330" className="payWithFixlyPromo__poster" />
        </picture>
      </section>
      <section className="payWithFixlyPromo__section" id="services">
        <Heading2 className="payWithFixlyPromo__heading">
          <div className="payWithFixlyPromo__row">Wybierz kategorię i znajdź fachowca na Fixly!</div>
        </Heading2>
        {data && (
          <ul className="payWithFixlyPromo__categoryList">
            {data.topCategoryList.map((item) => (
              <li key={item.slug}>
                {/* Note: Here server side navigation was made on purpose to use CDN cache */}
                <a
                  href={categoryUrlBySlug(item.slug)}
                  className="payWithFixlyPromo__pill"
                  onClick={onCategoryClick}
                  data-slug={categoryUrlBySlug(item.slug)}
                >
                  <span className="payWithFixlyPromo__icon">{categoryIcon(item.slug)}</span>
                  <span className="payWithFixlyPromo__spacer">{item.name}</span>
                  <ArrowRightIcon aria-hidden />
                </a>
              </li>
            ))}
          </ul>
        )}
      </section>
      <picture>
        <source srcSet={`${postReplacement1x} 1x, ${postReplacement2x} 2x`} type="image/webp" />
        <img src={postReplacement} alt="" width="330" height="330" className="payWithFixlyPromo__poster" />
      </picture>
      <PageScroller />
    </main>
  );
};
export default PayWithFixlyPromo;
